import { CHIP_DENOMS } from '@tv-casino/shared';
import { chipVars, deckVars, feltVars } from '../lib/themeStyle.js';
import type { CasinoOwnerPanelVM } from '@tv-casino/viewmodels/components/casinoOwnerPanelModel.js';
import { DeckPreview } from './DeckPreview.js';
import styles from './CasinoOwnerPanel.module.css';

interface CasinoOwnerPanelProps {
  vm: CasinoOwnerPanelVM;
  busy: boolean;
  onFelt: (felt: CasinoOwnerPanelVM['felts'][number]['id']) => void;
  onChips: (chips: CasinoOwnerPanelVM['chipSets'][number]['id']) => void;
  onDeck: (deck: CasinoOwnerPanelVM['decks'][number]['id']) => void;
}

/**
 * The venue-level counterpart to the host panel: the signed-in owner dresses
 * every table in the casino from their phone — the cloth on the felts, the
 * chips on the rails and the cards in the shoe. A change re-dresses this phone
 * straight away and rides `casino_state` to the TV and every other phone.
 */
export function CasinoOwnerPanel({ vm, busy, onFelt, onChips, onDeck }: CasinoOwnerPanelProps) {
  return (
    <details className={styles.panel}>
      <summary className={styles.summary}>Casino controls</summary>

      <div className={styles.body}>
        <section className={styles.group}>
          <p className={styles.groupTitle}>Table felt</p>
          <p className={styles.hint}>
            The cloth on every table at {vm.casinoName}, on the TV and on the phones sitting at
            them.
          </p>
          <div className={styles.swatches} role="group" aria-label="Table felt">
            {vm.felts.map((felt) => (
              <button
                key={felt.id}
                type="button"
                className={felt.active ? styles.swatchOn : styles.swatch}
                style={feltVars(felt.id)}
                aria-pressed={felt.active}
                disabled={busy}
                onClick={() => !felt.active && onFelt(felt.id)}
              >
                {felt.label}
              </button>
            ))}
          </div>
        </section>

        <section className={styles.group}>
          <p className={styles.groupTitle}>Chip set</p>
          <p className={styles.hint}>
            The chips on every phone's rail and tray here. Chips already on the felt keep their
            player's colour.
          </p>
          <div className={styles.swatches} role="group" aria-label="Chip set">
            {vm.chipSets.map((set) => (
              <button
                key={set.id}
                type="button"
                className={`${set.active ? styles.swatchOn : styles.swatch} ${styles.chipSwatch}`}
                style={chipVars(set.id)}
                aria-pressed={set.active}
                disabled={busy}
                onClick={() => !set.active && onChips(set.id)}
              >
                <span className={styles.chipRow} aria-hidden="true">
                  {CHIP_DENOMS.map((denom) => (
                    <span
                      key={denom}
                      className={styles.miniChip}
                      style={{
                        background: `radial-gradient(circle at 50% 35%, var(--chip${denom}-top), var(--chip${denom}-rim) 70%)`,
                        borderColor: `var(--chip${denom}-edge)`,
                      }}
                    />
                  ))}
                </span>
                {set.label}
              </button>
            ))}
          </div>
        </section>

        <section className={styles.group}>
          <p className={styles.groupTitle}>Card deck</p>
          <p className={styles.hint}>
            The cards dealt at every table here: the backs on the TV and the faces in every hand.
          </p>
          <div className={styles.swatches} role="group" aria-label="Card deck">
            {vm.decks.map((deck) => (
              <button
                key={deck.id}
                type="button"
                className={`${deck.active ? styles.swatchOn : styles.swatch} ${styles.deckSwatch}`}
                style={deckVars(deck.id)}
                aria-pressed={deck.active}
                disabled={busy}
                onClick={() => !deck.active && onDeck(deck.id)}
              >
                <DeckPreview />
                {deck.label}
              </button>
            ))}
          </div>
        </section>
      </div>
    </details>
  );
}
